import './PlaylistsOverlay.scss';
import React, { type ChangeEvent, useState } from 'react';
import { generalStore } from '../../store';
import { Link, useLocation, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import {
    ROUTES,
    SEARCH_DEBOUNCE_VALUE,
    ORDER_BY_DIRECTION,
    RESPONSE
} from '../../js/_constants';
import Helpers from '../../js/helpers';
import { type Playlist, type DistributedMedium } from '../../api/types/index';
import { type ResourceResponse } from '../../api/types/response';
import _server from '../../js/config/_server';
import Loader from '../loader/Loader';
import InputField from '../inputField/InputField';
import { apiPost } from '../../js/api';
import debounce from 'debounce';
import Paginator from '../paginator/Paginator';
import { BRAND_LETTER } from '../../js/config/_global';
import ItemCard from '../ItemCard/ItemCard';

type PlaylistsOverlayProps = {
    selectedMediaItems: DistributedMedium[];
    isLoading?: boolean;
    market?: boolean;
};

const PlaylistsOverlay = ({
    selectedMediaItems,
    market,
}: PlaylistsOverlayProps) => {
    const { t } = useTranslation();
    const { setAlert } = generalStore();

    // check if asset is one off the assigned assets to the playlist by id
    const isItemInPlaylist = (
        targetPlaylistItems,
        mediaItemId: string
    ) =>
        targetPlaylistItems.some(
            item => mediaItemId === item.medium
        );

    const [isUpdating, setIsUpdating] = useState(false);

    const [recentlyUpdatedPlaylists, setRecentlyUpdatedPlaylists] = useState<string[]>([]);

    const isRecentlyUpdated = playlist => recentlyUpdatedPlaylists.includes(playlist)

    const handleAdd = async (playlist: Playlist) => {
        setRecentlyUpdatedPlaylists([...recentlyUpdatedPlaylists, playlist[RESPONSE.ID]])
        const responses = [] as any;
        setIsUpdating(true);
        await Promise.all(
            selectedMediaItems
                .filter(
                    mediaItem =>
                        !isItemInPlaylist(playlist.items, mediaItem[RESPONSE.ID])
                )
                .map((mediaItem, i) => {
                    const index = playlist.items.length + i;
                    const url = `${_server.baseUrl}${
                        playlist[RESPONSE.ID]
                    }/items`;

                    const options = {
                        body: {
                            medium: mediaItem[RESPONSE.ID],
                            position: index + 1,
                            duration:
                                mediaItem['@type'].toLowerCase() === 'image'
                                    ? 15
                                    : mediaItem.duration
                                    ? mediaItem.duration
                                    : 0
                        }
                    };

                    return apiPost<ResourceResponse<Playlist>>(url, options).then((res)=>
                        responses.push(res)
                    );
                })
        ).finally(() =>{
            if(responses.every(res => typeof res === "object")) {
                setAlert({
                    message: t('add_to_playlist_successfully'),
                    type: 'info',
                    display: true,
                    position:'left'
                })
            }
            setIsUpdating(false)
        });
    };

    const [query, setQuery] = useState('');

    const handleSearch = (event: ChangeEvent<HTMLInputElement>) => {
        const { value } = event.target;
        setQuery(value);
    };

    const debouncedHandleSearch = debounce(handleSearch, SEARCH_DEBOUNCE_VALUE);

    return (
        <div className="b-playlists-overlay">
            <InputField
                label={t('searchInput')}
                name="searchInput"
                onChangeHandler={debouncedHandleSearch}
            />
            {query && (
                <span className="options-length-warning">
                    {t('search_order_guide')}
                </span>
            )}
            <div className="content-container">
                {isUpdating ? (
                    <Loader />
                ) : (
                        <Paginator<Playlist>
                            source={_server.services.playlists.userPlaylists}
                            preFetch
                            isUpdateLocationEnabled={false}
                            params={{
                                createdByCurrentUser: market ? false : true,
                                brand: BRAND_LETTER,
                                ...(query
                                    ? { title: query }
                                    : {
                                          'order[updatedAt]':
                                              ORDER_BY_DIRECTION.DESC
                                      })
                            }}
                        >
                            {playlists => (
                                <ul className="modal-list">
                                    {playlists &&
                                        playlists.map((playlist,i) => {
                                            return (
                                                <li key={playlist[RESPONSE.ID]}>
                                                    <ItemCard
                                                        className='playlist-card'
                                                        key={playlist[RESPONSE.ID]}
                                                        thumbnail={playlist.thumbnail}
                                                        icon={isRecentlyUpdated(playlist[RESPONSE.ID]) ? "select-circle" : "media-playlist"}
                                                        title={playlist.title}
                                                        footer={
                                                            <>
                                                                <p className="add-to" onClick={() => handleAdd(playlist)}>{t('add_to_playlist_footer')}{' '}+</p>
                                                                <Link target="_blank" to={`${ROUTES.PLAYLISTS}/${Helpers.getId(playlist)}`}>
                                                                    <p className="go-to">{t('go_to_playlist')}{' '}&gt;</p>
                                                                </Link>
                                                            </>
                                                        }
                                                    />                                               
                                                </li>
                                            );
                                        })}
                                </ul>
                            )}
                        </Paginator>
                )}
            </div>
        </div>
    );
};

export default PlaylistsOverlay;
